import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SearchIcon, Loader2, CheckIcon, MailIcon } from "lucide-react";
import { api } from "@/services/api";
import { toast } from "@/components/ui/use-toast";
import { getCache, setCache, CACHE_KEYS } from "@/services/cacheService";

interface User {
  id: string;
  name: string;
  email: string;
  department?: string;
  position?: string;
  initials?: string;
}

interface ShareDocumentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  documentName: string;
}

const ShareDocumentModal: React.FC<ShareDocumentModalProps> = ({
  open,
  onOpenChange,
  documentId,
  documentName,
}) => {
  const { t } = useTranslation(['documents', 'common']);
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [manualEmails, setManualEmails] = useState<string[]>([]);
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSelectedUsers([]);
      setManualEmails([]);
    }
  }, [open]);

  const loadUsers = async () => {
    const cached = getCache<User[]>(CACHE_KEYS.USERS);
    if (cached) {
      setUsers(cached);
      return;
    }
    setIsLoading(true);
    try {
      const response = await api.get<User[]>("/users") as any;
      const data = Array.isArray(response) ? response : response.data || [];
      const list = Array.isArray(data) ? data : [];
      setUsers(list);
      setCache(CACHE_KEYS.USERS, list);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && searchQuery.trim().length >= 3 && users.length === 0 && !isLoading) {
      loadUsers();
    }
  }, [open, searchQuery]);

  const filteredUsers = useMemo(() => {
    if (searchQuery.trim().length < 3) return [];
    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        (user.department && user.department.toLowerCase().includes(query)) ||
        (user.position && user.position.toLowerCase().includes(query))
    );
  }, [users, searchQuery]);

  const toggleUser = (user: User) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((u) => u.id === user.id);
      if (isSelected) {
        return prev.filter((u) => u.id !== user.id);
      }
      return [...prev, user];
    });
  };

  const isUserSelected = (userId: string) =>
    selectedUsers.some((u) => u.id === userId);

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isEmailAlreadyAdded = (email: string) =>
    manualEmails.includes(email.toLowerCase()) ||
    selectedUsers.some((u) => u.email.toLowerCase() === email.toLowerCase());

  const addManualEmail = () => {
    const email = searchQuery.trim().toLowerCase();
    if (!isValidEmail(email)) return;
    if (isEmailAlreadyAdded(email)) {
      toast({ title: t('documents:viewer_share_already_added'), description: t('documents:viewer_share_already_added_desc') });
      return;
    }
    setManualEmails((prev) => [...prev, email]);
    setSearchQuery("");
  };

  const removeManualEmail = (email: string) => {
    setManualEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addManualEmail();
    }
  };

  const showAddEmailButton = searchQuery.trim() && isValidEmail(searchQuery.trim()) && !isEmailAlreadyAdded(searchQuery.trim());

  const totalRecipients = selectedUsers.length + manualEmails.length;

  const handleShare = async () => {
    if (totalRecipients === 0) return;

    setIsSharing(true);
    try {
      await api.post(`/documents/${documentId}/share`, {
        user_ids: selectedUsers.map((u) => u.id),
        emails: manualEmails,
      });

      toast({
        title: t('documents:viewer_toast_shared'),
        description: t('documents:viewer_toast_shared_desc', { count: totalRecipients }),
      });
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error sharing document:", error);
      toast({
        title: t('documents:viewer_toast_share_failed'),
        description: error.message || t('documents:viewer_toast_share_failed_desc'),
        variant: "destructive",
      });
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('documents:viewer_share_title')}</DialogTitle>
          <DialogDescription>
            {t('documents:viewer_share_description', { name: documentName })}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('documents:viewer_share_search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="pl-9"
          />
        </div>

        {showAddEmailButton && (
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start text-sm"
            onClick={addManualEmail}
          >
            <MailIcon className="h-4 w-4 mr-2 text-muted-foreground" />
            {t('documents:viewer_share_add_email', { email: searchQuery.trim() })}
          </Button>
        )}

        {totalRecipients > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selectedUsers.map((user) => (
              <span
                key={user.id}
                className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full cursor-pointer hover:bg-primary/20"
                onClick={() => toggleUser(user)}
              >
                {user.name}
                <span className="text-primary/60">&times;</span>
              </span>
            ))}
            {manualEmails.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full cursor-pointer hover:bg-orange-200"
                onClick={() => removeManualEmail(email)}
              >
                <MailIcon className="h-3 w-3" />
                {email}
                <span className="text-orange-400">&times;</span>
              </span>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto min-h-0 max-h-[300px] border rounded-md">
          {searchQuery.trim().length < 3 ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-sm text-muted-foreground">
                {t('documents:viewer_share_min_chars')}
              </span>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">{t('documents:viewer_share_loading')}</span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-sm text-muted-foreground">
                {t('documents:viewer_share_no_results')}
              </span>
            </div>
          ) : (
            <div className="divide-y">
              {filteredUsers.map((user) => {
                const selected = isUserSelected(user.id);
                return (
                  <div
                    key={user.id}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 transition-colors ${
                      selected ? "bg-primary/5" : ""
                    }`}
                    onClick={() => toggleUser(user)}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">
                        {user.initials || user.name.split(" ").map((n) => n[0]).join("").toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      {(user.department || user.position) && (
                        <p className="text-xs text-muted-foreground truncate">
                          {[user.position, user.department].filter(Boolean).join(" · ")}
                        </p>
                      )}
                    </div>
                    {selected && (
                      <CheckIcon className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSharing}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleShare}
            disabled={totalRecipients === 0 || isSharing}
          >
            {isSharing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('documents:viewer_share_sharing')}
              </>
            ) : (
              t('documents:viewer_share_button', { count: totalRecipients })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDocumentModal;
